import ContainerScreen from '@/main/components/shared/containerScreen/containerLayout';
import { AreaButton, KOGAreaButtons } from '@/main/components/shared/styles';
import { RoutesConstants } from '@/main/routes/routesConstants';
import {
  getDefaultOptionDrop,
  getTranslateByKey,
  hasAccessByRoute, mountReducerWithData
} from '@/main/services/utils';
import Button from '@/main/components/shared/button/button';
import * as S from './styles';
import { useEffect, useMemo, useState } from 'react';
import useFormQueryParams from '@/main/hooks/useformQueryParams';
import { KeyValue } from '@/main/models/common';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import { useDispatch } from 'react-redux';
import {
  getCompanyType,
  useCompanyTypeReducer
} from '@/main/store/companyType';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import {
  getSystemApplication,
  useSystemApplicationReducer
} from '@/main/store/systemApplication';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';
import KogInputAutoCompleteCheckControlled from '@/main/components/shared/inputAutoCompleteCheck/inputAutoCompleteCheckControlled';
import { isNil } from 'lodash';
import { getOptionsStatusActiveGeneral } from '@/main/services/data/data';
import { StatusActiveGeneralEnum } from '@/main/services/enum/StatusActiveGeneralEnum';
import { getCulture, useCultureReducer } from '@/main/store/culture';
import { getStatusDomain } from '@/main/store/announcement';
import SystemGuideRegisterSidebar from '@/main/screens/registration/systemGuide/systemGuideRegisterSidebar';
import {
  getInsurers,
  resetReducerInsurers,
  useInsurerReducer
} from '@/main/store/insurer';
import { InsurerFilterRequest } from '@/main/models/request';
import { HeadQuarterBranchFilterEnum } from '@/main/services/enum/HeadQuarterBranchFilterEnum';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import SystemGuideSearchGrid, {
  INITIAL_SYSTEM_GUIDE_GRID_STATE,
  ISystemGuideGridData
} from '@/main/screens/registration/systemGuide/systemGuideSearchGrid';
import { SystemGuideSearchGridResponse } from '@/main/models/response/systemGuideResponse';
import { SystemGuideSearchRequest } from '@/main/models/request/systemGuideRequest';
import { userIsInsurer } from '@/main/services/utilsUser';
import { systemGuideApi } from '@/main/api';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';

const ID_BASE_COMPONENTS = 'id-searchSystemGuide';

export default function SystemGuideSearchScreen() {
  const dispatch = useDispatch();

  const systemApplicationReducer = useSystemApplicationReducer();
  const countryReducer = useCountryReducer();
  const companyTypeReducer = useCompanyTypeReducer();
  const optionsStatus = getOptionsStatusActiveGeneral();
  const { culture: cultureReducer } = useCultureReducer();
  const { insurers: insurerReducer } = useInsurerReducer();

  const optionDefaultCountryDrop = getDefaultOptionDrop(
    countryReducer.countriesUser.data
  );

  const optionDefaultInsurerDrop = userIsInsurer()
    ? getDefaultOptionDrop(insurerReducer.data)
    : null;
  const disabledInsurer = userIsInsurer();

  const [openSidebarNew, setOpenSidebarNew] = useState<boolean>(false);
  const [showFilters, setShowFilters] = useState<boolean>(true);
  const [formFilters, setFormFilters] = useState<ISearchSystemGuidForm | null>(
    null
  );
  const [gridData, setGridData] = useState<ISystemGuideGridData>(
    INITIAL_SYSTEM_GUIDE_GRID_STATE
  );
  const [systemGuideSelected, setSystemGuideSelected] =
    useState<SystemGuideSearchGridResponse>();

  const INITIAL_FORM_STATE = {
    country: null as KeyValue | null,
    companyType: null as KeyValue | null,
    language: [] as KeyValue[] | null,
    systemApplication: null as KeyValue | null,
    whoCanSee: [] as KeyValue[] | null,
    status: null as KeyValue | null,
    insurer: null as KeyValue | null
  };

  type ISearchSystemGuidForm = typeof INITIAL_FORM_STATE;

  const {
    control,
    setValue,
    customHandleSubmit,
    reset,
    clearErrors,
    watch,
    formState: { errors }
  } = useFormQueryParams<ISearchSystemGuidForm>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE,
    onSubmit: onSubmit,
    mapDefaultValues: {
      country: countryReducer.countriesUser,
      companyType: companyTypeReducer.companyType,
      language: cultureReducer,
      systemApplication: systemApplicationReducer.systemApplication,
      whoCanSee: companyTypeReducer.companyType,
      status: mountReducerWithData(optionsStatus)
    } as any
  });

  const countryWatched = watch('country');
  const systemApplicationWatched = watch('systemApplication');
  const statusWatched = watch('status');

  useEffect(() => {
    dispatch(getSystemApplication());
    dispatch(getStatusDomain(true));
    dispatch(getCountriesUser());
    dispatch(getCompanyType());
  }, []);

  useEffect(() => {
    if (!isNil(countryWatched)) {
      dispatch(
        getCulture({
          guidCountry: String(countryWatched.key)
        })
      );
    }
  }, [countryWatched]);

  useEffect(() => {
    callInsurerApiDomain();
  }, [countryReducer.countriesUser.data]);

  function callInsurerApiDomain() {
    dispatch(resetReducerInsurers());

    if (optionDefaultCountryDrop !== null) {
      const request: InsurerFilterRequest = {
        guidCountry: String(optionDefaultCountryDrop?.key),
        type: HeadQuarterBranchFilterEnum.All,
        idStatusRegister: StatusRegisterEnum.Active
      };

      dispatch(getInsurers(request));
    }
  }

  const optionDefaultStatus = useMemo(() => {
    const statusValue = optionsStatus.find(
      item => item.key === StatusActiveGeneralEnum.Active
    );

    return statusValue ?? null;
  }, [optionsStatus]);

  const optionDefaultSystemApplication = useMemo(() => {
    const resultData = systemApplicationReducer.systemApplication.data;

    if (isNil(resultData)) return null;

    const findClaims = resultData.find(
      item => item.key === SystemApplicationEnum.Claims.toString()
    );

    return findClaims ?? null;
  }, [systemApplicationReducer.systemApplication.data]);

  const optionWhoCanSee = useMemo(() => {
    const companyTypeIgnore = [CompanyTypeEnum.Admin];
    const resultCompanyTypes = companyTypeReducer.companyType.data;

    if (isNil(resultCompanyTypes) || resultCompanyTypes.length == 0) {
      return [];
    }

    return (
      resultCompanyTypes
        .filter(x => !companyTypeIgnore.includes(Number(x.key)))
        .map(item => ({
          key: item.key,
          value: item.value
        })) || []
    );
  }, [
    companyTypeReducer.companyType.data,
    companyTypeReducer.companyType.isLoading
  ]);

  const optionLanguage = useMemo(() => {
    const resultLanguages = cultureReducer.data;

    if (isNil(resultLanguages) || resultLanguages.length == 0) {
      return [];
    }

    return (
      resultLanguages.map(item => ({
        key: item.key,
        value: item.value
      })) || []
    );
  }, [cultureReducer.data, cultureReducer.isLoading]);

  const isDisableSearchButton = () =>
    systemApplicationReducer.systemApplication.isLoading ||
    countryReducer.countriesUser.isLoading ||
    companyTypeReducer.companyType.isLoading ||
    cultureReducer.isLoading ||
    insurerReducer.isLoading;

  function isDisabledCountry() {
    return countryReducer.countriesUser.data?.length === 1;
  }

  function handleFilters() {
    setShowFilters(!showFilters);
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }

  function handleNew() {
    setOpenSidebarNew(true);
  }

  function onSubmit(formInputs: ISearchSystemGuidForm) {
    setGridData({
      isLoading: true,
      totalItems: 0,
      data: []
    });

    setFormFilters(formInputs);

    const request: SystemGuideSearchRequest = {
      pageIndex: PaginationEnum.PageIndexIntial,
      pageSize: PaginationEnum.PageSize30,
      guidCountry: String(formInputs.country?.key),
      idSystemApplication: Number(formInputs.systemApplication?.key),
      guidCulture: formInputs.language?.map(item => String(item.key)) || [],
      guidInsurer: formInputs.insurer?.key
        ? String(formInputs.insurer.key)
        : undefined,
      active: formInputs.status
        ? Number(formInputs.status.key) === StatusActiveGeneralEnum.Active
        : undefined,
      whoCanSeeCompanyTypeIds:
        formInputs.whoCanSee?.map(item => Number(item.key)) || [],
      defaultCompany: false
    };

    systemGuideApi
      .getSearchGrid(request)
      .then(response => {
        let resultData: SystemGuideSearchGridResponse[] = [];
        let resultTotalItems: number = 0;
        if (response.data) {
          resultData = response.data.results;
          resultTotalItems = response.data.totalNumberOfRecords;
        }
        setGridData({
          isLoading: false,
          totalItems: resultTotalItems,
          data: resultData
        });
      })
      .finally(() => setGridData(crr => ({ ...crr, isLoading: false })));

    setShowFilters(false);
  }

  function onReset() {
    reset(INITIAL_FORM_STATE);

    setValue('country', optionDefaultCountryDrop);
    setValue('insurer', null);
    setValue('companyType', null);
    setValue('language', null);
    setValue('systemApplication', optionDefaultSystemApplication);
    setValue('whoCanSee', null);
    setValue('status', optionDefaultStatus);

    setFormFilters(null);
    setGridData(INITIAL_SYSTEM_GUIDE_GRID_STATE);

    clearErrors();
  }

  function editItem(
    value: React.SetStateAction<SystemGuideSearchGridResponse | undefined>
  ) {
    setSystemGuideSelected(value);
    setOpenSidebarNew(true);
  }

  function closeSidebar() {
    setOpenSidebarNew(false);
    setSystemGuideSelected(undefined);
  }

  function refreshSearchGrid() {
    if (formFilters) {
      onSubmit(formFilters);
    }
  }

  return (
    <>
      <ContainerScreen
        hasBackground
        title={`
        ${getTranslateByKey('information.registration')}
        ${' > '}
        ${getTranslateByKey('information.searchSystemGuide')}`}>
        <S.Container>
          <AreaButton>
            <Button
              onClick={handleFilters}
              id={`${ID_BASE_COMPONENTS}-button-filters`}>
              {getTranslateByKey('information.filters')}
            </Button>
            {hasAccessByRoute(RoutesConstants.registrationSearchGuid) && (
              <Button
                onClick={handleNew}
                id={`${ID_BASE_COMPONENTS}-button-newSystemGuide`}>
                {getTranslateByKey('information.newSystemGuide')}
              </Button>
            )}
          </AreaButton>
          <Box
            showBox={showFilters}
            title={getTranslateByKey('information.filters')}>
            <form onSubmit={customHandleSubmit} noValidate>
              <S.RowInputs>
                <KogAutoCompleteControlled
                  label={getTranslateByKey('information.country')}
                  control={control}
                  nameCustom="country"
                  rulesCustom={{ required: true }}
                  errorMessageCustom={
                    errors.country?.type === 'required'
                      ? getTranslateByKey('information.countryRequired', true)
                      : ''
                  }
                  hasErrorCustom={!!errors.country}
                  options={countryReducer.countriesUser.data}
                  isLoading={countryReducer.countriesUser.isLoading}
                  onChangeCustom={() => {}}
                  disabled={isDisabledCountry()}
                  autoHighlightCustom={
                    countryReducer.countriesUser.data?.length === 1
                  }
                  setValueCustom={setValue}
                  id={`${ID_BASE_COMPONENTS}-select-country`}
                />

                <KogAutoCompleteControlled
                  id={`${ID_BASE_COMPONENTS}-select-insurer`}
                  nameCustom="insurer"
                  label={getTranslateByKey('information.insurer')}
                  isDefaultValueUnique={optionDefaultInsurerDrop !== null}
                  control={control}
                  setValueCustom={setValue}
                  options={insurerReducer.data}
                  isLoading={insurerReducer.isLoading}
                  disabled={disabledInsurer}
                  onChangeCustom={() => {}}
                />

                <KogAutoCompleteControlled
                  id={`${ID_BASE_COMPONENTS}-select-companyType`}
                  nameCustom="companyType"
                  label={getTranslateByKey('information.companyType')}
                  isDefaultValueUnique
                  control={control}
                  setValueCustom={setValue}
                  options={companyTypeReducer.companyType.data}
                  isLoading={companyTypeReducer.companyType.isLoading}
                  onChangeCustom={() => {}}
                />

                <KogInputAutoCompleteCheckControlled
                  id={`${ID_BASE_COMPONENTS}-muiltselect-language`}
                  label={getTranslateByKey('information.language')}
                  name="language"
                  control={control}
                  setValueCustom={setValue}
                  options={optionLanguage}
                  isLoading={cultureReducer.isLoading}
                  onChangeCustom={() => {}}
                  disableSelectAll
                />

                <KogAutoCompleteControlled
                  id={`${ID_BASE_COMPONENTS}-select-system`}
                  label={getTranslateByKey('information.systemApplication')}
                  control={control}
                  nameCustom="systemApplication"
                  rulesCustom={{ required: true }}
                  required
                  errorMessageCustom=""
                  hasErrorCustom={false}
                  valueDefaultKey={optionDefaultSystemApplication?.key}
                  options={systemApplicationReducer.systemApplication.data}
                  isLoading={
                    systemApplicationReducer.systemApplication.isLoading
                  }
                  disabled
                  onChangeCustom={() => {}}
                  setValueCustom={setValue}
                />

                <KogInputAutoCompleteCheckControlled
                  id={`${ID_BASE_COMPONENTS}-txt-whoCanSee`}
                  label={getTranslateByKey('information.whoCanSee')}
                  name="whoCanSee"
                  control={control}
                  setValueCustom={setValue}
                  options={optionWhoCanSee}
                  isLoading={companyTypeReducer.companyType.isLoading}
                  onChangeCustom={() => {}}
                  disableSelectAll
                />

                <KogAutoCompleteControlled
                  id={`${ID_BASE_COMPONENTS}-select-status`}
                  nameCustom="status"
                  label={getTranslateByKey('information.status')}
                  control={control}
                  setValueCustom={setValue}
                  options={optionsStatus}
                  disabled={false}
                  onChangeCustom={() => {}}
                  valueDefaultKey={optionDefaultStatus?.key}
                />
              </S.RowInputs>

              <KOGAreaButtons>
                <SecondaryButton
                  id={`${ID_BASE_COMPONENTS}-button-search`}
                  type="submit"
                  disabled={isDisableSearchButton()}>
                  {getTranslateByKey('information.search')}
                </SecondaryButton>
                <SecondaryButton
                  id={`${ID_BASE_COMPONENTS}-button-clear`}
                  onClick={onReset}>
                  {getTranslateByKey('information.clear')}
                </SecondaryButton>
              </KOGAreaButtons>
            </form>
          </Box>
        </S.Container>

        <SystemGuideSearchGrid
          formFilters={formFilters}
          gridData={gridData}
          setGridData={setGridData}
          setSystemGuideSelected={editItem}
        />
      </ContainerScreen>

      <SystemGuideRegisterSidebar
        open={openSidebarNew}
        closeSidebar={closeSidebar}
        refreshSearchGrid={refreshSearchGrid}
        systemGuide={systemGuideSelected}
      />
    </>
  );
}
